import {
  Modal,
  Stack,
  Box,
  Typography,
  IconButton,
  Alert,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { desktopSx } from "src/utils/styles/modalStyles";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { OptionState } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { useCreateIntakeQuestionOptionMutation } from "src/api/services/intakeQuestionOptionsApi";
import AddIcon from "@mui/icons-material/Add";
import IntakeQuestionOptionView from "src/components/IntakeQuestionOptionView";

const initialOptionState: OptionState[] = [
  {
    position: 0,
    questionOption: null,
    nextQuestion: null,
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  intakeQuestionId: number;
  intakeId: number;
}

const AddNewIntakeQuestionOption = ({
  onClose,
  open,
  intakeQuestionId,
  intakeId,
}: Props) => {
  const [createQuestionOption, { isLoading }] =
    useCreateIntakeQuestionOptionMutation();
  const logError = useSentryLogger("error");
  const [error, setError] = useState("");

  const [options, setOptions] = useState<OptionState[]>(initialOptionState);

  const addNewOption = useCallback(() => {
    setOptions((prev) => [
      ...prev,
      { position: prev.length, questionOption: null, nextQuestion: null },
    ]);
  }, []);

  const body = useMemo(() => {
    return options
      .filter((opt) => opt.questionOption != null)
      .map((opt) => ({
        option_id: opt.questionOption!.id,
        sort_order: opt.position,
        next_question: opt.nextQuestion?.slug,
      }));
  }, [options]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setError("");

      if (options.some((opt) => opt.questionOption == null)) {
        setError("Please select a question option for all options");
        return;
      }

      try {
        await createQuestionOption({
          intakeId,
          questionId: intakeQuestionId,
          body,
        });

        setOptions(initialOptionState);
        onClose();
      } catch (e) {
        logError("Failed to create intake question", e);
        setError("Failed to create intake question");
      }
    },
    [
      body,
      createQuestionOption,
      intakeId,
      intakeQuestionId,
      logError,
      onClose,
      options,
    ],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Question Options
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        <Stack gap={2}>
          {options.map((option, index) => (
            <IntakeQuestionOptionView
              key={index}
              index={index}
              option={option}
              setOption={setOptions}
              setError={setError}
            />
          ))}
          <Tooltip title="Add another question option">
            <IconButton
              onClick={addNewOption}
              color="primary"
              sx={{ alignSelf: "flex-end" }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>

          <LoadingButton type="submit" loading={isLoading}>
            Create options
          </LoadingButton>
        </Stack>
        {error ? <Alert severity={"error"}>{error}</Alert> : null}
      </Stack>
    </Modal>
  );
};

export default AddNewIntakeQuestionOption;
